import React from 'react';
import { DataGrid, Column } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import { Card } from '../ui/card';
import { SpreadsheetProps, SpreadsheetData } from './types';
import { numberToColumnLetter, getCellKey } from './utils';
import {
  useSpreadsheetData,
  useSpreadsheetSelection,
  useSpreadsheetSearch,
  useSpreadsheetCopy,
  useSpreadsheetSelectAll,
  useSpreadsheetSelectionCSVExport,
  useSpreadsheetDimensions,
  useSpreadsheetNavigation
} from './hooks';
import {
  SpreadsheetSearchBar,
  SpreadsheetCell,
  SpreadsheetContextMenu
} from './components';

export function Spreadsheet({
  columns = 10,
  rows = 20,
  data = [],
  editable = true,
  isCellEditable,
  onDataChange,
  onSelectionChange,
  onCopy,
  onCellBlur,
  columnWidths,
  rowHeight = 35,
  maxHeight,
  fillHeight = false,
  isFullscreen = false,
  buffers = { fullscreen: 0, normal: 0 },
  className = '',
  showRowNumbers = true,
  showColumnHeaders = true,
  getCellStyle,
  autoFillSpace = false,
  minColumns = 1,
  minRows = 1,
  customColumnHeaders,
  searchTerm,
  onSearchChange,
  currentSearchIndex,
  totalSearchResults,
  onNextSearch,
  onPreviousSearch,
  onClearSearch,
  dropdownOptions,
  textareaColumns,
  checkboxColumns,
  datetimeColumns,
  commandPopoverColumns,
  commandPopoverOptions,
  csvFilename,
  csvIncludeHeaders = true,
  onCSVExportStart,
  onCSVExportSuccess,
  onCSVExportError,
  frozenColumnCount,
  customContextMenuItems,
  getHeaderStyle
}: SpreadsheetProps) {
  // Generate unique ID for this spreadsheet instance
  const spreadsheetId = React.useMemo(() => `spreadsheet-${Math.random().toString(36).substr(2, 9)}`, []);

  // Dimensions and layout
  const {
    containerRef,
    calculatedColumns,
    calculatedRows,
    getSpreadsheetHeight
  } = useSpreadsheetDimensions({
    columns,
    rows,
    data,
    autoFillSpace,
    minColumns,
    minRows,
    columnWidths,
    rowHeight,
    showRowNumbers,
    showColumnHeaders,
    fillHeight,
    isFullscreen,
    buffers,
    maxHeight
  });

  // Data management
  const {
    spreadsheetData,
    handleRowsChange
  } = useSpreadsheetData({
    data,
    rows,
    columns,
    calculatedRows,
    calculatedColumns,
    autoFillSpace,
    onDataChange
  });

  // Selection management
  const {
    selectedCells,
    activeCell,
    setSelectedCells,
    setSelectionStart,
    setActiveCell,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleMouseUp,
    navigateToCell
  } = useSpreadsheetSelection({
    onSelectionChange
  });

  // Search functionality
  const {
    effectiveSearchTerm,
    effectiveCurrentIndex,
    effectiveTotalResults,
    searchResults,
    showSearchBar,
    setShowSearchBar,
    handleSearchChange,
    handleNextSearch,
    handlePreviousSearch,
    handleClearSearch,
    isCellSearchMatch,
    isCellCurrentSearchResult,
    navigateToSearchIndex
  } = useSpreadsheetSearch({
    spreadsheetData,
    searchTerm,
    onSearchChange,
    currentSearchIndex,
    totalSearchResults,
    onNextSearch,
    onPreviousSearch,
    onClearSearch
  });

  // Copy functionality
  const { handleCopyKeyDown } = useSpreadsheetCopy({
    selectedCells,
    spreadsheetData,
    onCopy
  });

  // Select all functionality
  const { handleSelectAllKeyDown } = useSpreadsheetSelectAll({
    spreadsheetData,
    customColumnHeaders,
    onSelectAll: React.useCallback((allCells: Set<string>) => {
      setSelectedCells(allCells);
      // Set the active cell to the first cell (A1) when selecting all
      setActiveCell({ rowIdx: 0, columnKey: 'A' });
      setSelectionStart({ rowIdx: 0, columnKey: 'A' });
    }, [setSelectedCells, setActiveCell, setSelectionStart])
  });

  // Simple cell blur handler (no save functionality)
  const handleCellBlurWithSave = React.useCallback((
    rowIdx: number,
    columnKey: string,
    value: any,
    onCellBlur?: (rowIdx: number, columnKey: string, value: any) => void
  ) => {
    if (onCellBlur) {
      onCellBlur(rowIdx, columnKey, value);
    }
  }, []);

  // Navigation
  const {
    ensureContainerFocus,
    handleKeyDown,
    handleInputKeyDown
  } = useSpreadsheetNavigation({
    rows,
    columns,
    calculatedRows,
    calculatedColumns,
    autoFillSpace,
    activeCell,
    navigateToCell,
    containerRef
  });

  // Selection CSV Export functionality
  const { exportSelectionToCSV } = useSpreadsheetSelectionCSVExport({
    selectedCells,
    spreadsheetData,
    customColumnHeaders,
    filename: csvFilename ? csvFilename.replace('.csv', '-selection.csv') : undefined,
    includeHeaders: csvIncludeHeaders,
    onExportStart: onCSVExportStart,
    onExportSuccess: onCSVExportSuccess,
    onExportError: onCSVExportError
  });

  // Context menu state
  const [contextMenu, setContextMenu] = React.useState<{
    isVisible: boolean;
    position: { x: number; y: number };
    rightClickedRowData: SpreadsheetData | null;
    rightClickedRowIndex: number;
    rightClickedColumnKey?: string;
  }>({
    isVisible: false,
    position: { x: 0, y: 0 },
    rightClickedRowData: null,
    rightClickedRowIndex: -1,
    rightClickedColumnKey: undefined
  });

  // Enhanced navigation with search support
  const enhancedHandleNextSearch = React.useCallback(() => {
    if (searchResults.length > 0) {
      const nextIndex = (effectiveCurrentIndex + 1) % searchResults.length;
      // Use navigateToSearchIndex which handles both scrolling and state updates
      navigateToSearchIndex(nextIndex, containerRef, rowHeight, columnWidths, showRowNumbers);
      // Also update cell selection
      const result = searchResults[nextIndex];
      if (result) {
        navigateToCell(result);
      }
    }
    // Call the original handler after our navigation
    handleNextSearch();
  }, [handleNextSearch, effectiveCurrentIndex, navigateToCell, searchResults, navigateToSearchIndex, containerRef, rowHeight, columnWidths, showRowNumbers]);

  const enhancedHandlePreviousSearch = React.useCallback(() => {
    if (searchResults.length > 0) {
      const prevIndex = effectiveCurrentIndex <= 0 ? searchResults.length - 1 : effectiveCurrentIndex - 1;
      // Use navigateToSearchIndex which handles both scrolling and state updates
      navigateToSearchIndex(prevIndex, containerRef, rowHeight, columnWidths, showRowNumbers);
      // Also update cell selection
      const result = searchResults[prevIndex];
      if (result) {
        navigateToCell(result);
      }
    }
    // Call the original handler after our navigation
    handlePreviousSearch();
  }, [handlePreviousSearch, effectiveCurrentIndex, navigateToCell, searchResults, navigateToSearchIndex, containerRef, rowHeight, columnWidths, showRowNumbers]);

  // Keyboard shortcuts for search
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle Ctrl+F if this spreadsheet container has focus
      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'f') {
          // Check if the event target is within our spreadsheet container
          const target = event.target as Element;
          if (containerRef.current && containerRef.current.contains(target)) {
            event.preventDefault();
            setShowSearchBar(true);
          }
        }
      }
      
      // Handle search navigation only if search bar is visible
      if (showSearchBar && effectiveSearchTerm && containerRef.current) {
        const target = event.target as Element;
        
        // Only handle search navigation when the container itself has focus
        if (target === containerRef.current) {
          if (event.key === 'Enter') {
            event.preventDefault();
            if (event.shiftKey) {
              enhancedHandlePreviousSearch();
            } else {
              enhancedHandleNextSearch();
            }
          } else if (event.key === 'Escape') {
            event.preventDefault();
            handleClearSearch();
            setShowSearchBar(false);
          }
        }
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (showSearchBar && containerRef.current) {
        const searchBar = containerRef.current.querySelector(`[data-spreadsheet-id="${spreadsheetId}"]`);
        const target = event.target as Element;
        
        // Only close if clicking outside both the search bar and the spreadsheet container
        if (searchBar && !searchBar.contains(target) && !containerRef.current.contains(target)) {
          setShowSearchBar(false);
          // Return focus to the spreadsheet container
          if (containerRef.current) {
            containerRef.current.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchBar, effectiveSearchTerm, spreadsheetId, containerRef, enhancedHandleNextSearch, enhancedHandlePreviousSearch, handleClearSearch, setShowSearchBar]);

  // Handle spreadsheet-level keyboard events
  const handleSpreadsheetKeyDown = (event: React.KeyboardEvent) => {
    // Handle copy operation
    if (handleCopyKeyDown(event, document.activeElement || undefined)) {
      return;
    }

    // Handle select all operation
    if (handleSelectAllKeyDown(event, document.activeElement || undefined)) {
      return;
    }

    // Handle other keyboard navigation
    handleKeyDown(event);
  };

  const handleContainerMouseDown = (event: React.MouseEvent) => {
    const target = event.target as Element;
    
    // Only avoid stealing focus if clicking directly on the search input
    if (target.tagName === 'INPUT' && target.closest(`[data-spreadsheet-id="${spreadsheetId}"]`)) {
      return;
    }
    
    ensureContainerFocus();
  };

  const handleContainerClick = (event: React.MouseEvent) => {
    const target = event.target as Element;
    
    // Only avoid stealing focus if clicking directly on the search input
    if (target.tagName === 'INPUT' && target.closest(`[data-spreadsheet-id="${spreadsheetId}"]`)) {
      return;
    }
    
    // Close context menu on click
    if (contextMenu.isVisible) {
      closeContextMenu();
    }
    
    ensureContainerFocus();
  };

  // Handle right-click context menu
  const handleContextMenu = (event: React.MouseEvent) => {
    // Only show context menu if there are selected cells
    if (selectedCells.size > 0) {
      event.preventDefault();
      
      // Try to find the row index and column key from the right-clicked element
      let rightClickedRowIndex = -1;
      let rightClickedRowData: SpreadsheetData | null = null;
      let rightClickedColumnKey: string | undefined = undefined;
      
      // Look for row and column data attributes in the event target or its parents
      let target = event.target as Element;
      while (target && target !== event.currentTarget) {
        const rowAttr = target.getAttribute('data-row');
        const colAttr = target.getAttribute('data-col');
        
        if (rowAttr !== null) {
          rightClickedRowIndex = parseInt(rowAttr, 10);
          if (rightClickedRowIndex >= 0 && rightClickedRowIndex < spreadsheetData.length) {
            rightClickedRowData = spreadsheetData[rightClickedRowIndex];
          }
        }
        
        if (colAttr !== null) {
          rightClickedColumnKey = colAttr;
        }
        
        // If we found both row and column info, we can break
        if (rowAttr !== null && colAttr !== null) {
          break;
        }
        
        target = target.parentElement!;
      }
      
      // Position the context menu at the mouse cursor
      setContextMenu({
        isVisible: true,
        position: {
          x: event.clientX,
          y: event.clientY
        },
        rightClickedRowData,
        rightClickedRowIndex,
        rightClickedColumnKey
      });
    }
  };

  // Close context menu
  const closeContextMenu = () => {
    setContextMenu(prev => ({ ...prev, isVisible: false }));
  };

  // Cell event handlers
  const handleCellChange = React.useCallback((rowIdx: number, columnKey: string, value: any) => {
    const newRow = { ...spreadsheetData[rowIdx], [columnKey]: value };
    const newData = [...spreadsheetData];
    newData[rowIdx] = newRow;
    handleRowsChange(newData);
  }, [spreadsheetData, handleRowsChange]);

  const handleCellFocus = React.useCallback((rowIdx: number, columnKey: string) => {
    const cellPosition = { rowIdx, columnKey };
    const cellKey = getCellKey(rowIdx, columnKey);
    
    if (!selectedCells.has(cellKey)) {
      navigateToCell(cellPosition);
    }
  }, [selectedCells, navigateToCell]);

  // Generate column definitions
  const columnDefs: Column<SpreadsheetData>[] = [
    // Row number column (conditionally included)
    ...(showRowNumbers ? [{
      key: '__rowNumber',
      name: '',
      width: 50,
      resizable: false,
      sortable: false,
      frozen: frozenColumnCount ? true : false,
      cellClass: 'spreadsheet-row-header',
      renderCell: ({ rowIdx }: { rowIdx: number }) => (
        <div className="flex items-center justify-center h-full bg-gray-50 text-gray-600 font-medium text-sm" style={{borderRight: '0.5px solid #D4D4D4'}}>
          {rowIdx + 1}
        </div>
      ),
    }] : []),
    // Data columns
    ...Array.from({ length: autoFillSpace ? calculatedColumns : columns }, (_, index) => {
      const columnKey = numberToColumnLetter(index);
      const customHeader = customColumnHeaders?.[index];
      const displayName = showColumnHeaders ? (customHeader || columnKey) : '';
      
      return {
        key: columnKey,
        name: displayName,
        width: columnWidths?.[index] || 100,
        resizable: true,
        sortable: false,
        editable: editable,
        frozen: (frozenColumnCount && index < frozenColumnCount) ? true : false,
        renderHeaderCell: showColumnHeaders ? () => {
          const headerText = customHeader || columnKey;
          const customStyle = getHeaderStyle ? getHeaderStyle(columnKey, headerText) : undefined;
          
          return (
            <div 
              className="flex items-center justify-center h-full bg-gray-50 font-medium text-sm text-gray-600"
              style={customStyle}
            >
              {headerText}
            </div>
          );
        } : undefined,
        renderCell: ({ row, rowIdx }) => {
          const value = row[columnKey];
          const cellKey = getCellKey(rowIdx, columnKey);
          const isSelected = selectedCells.has(cellKey);
          const isActive = activeCell?.rowIdx === rowIdx && activeCell?.columnKey === columnKey;
          const customStyle = getCellStyle ? getCellStyle(rowIdx, columnKey, value) : undefined;
          
          // Check if this cell is a search match
          const isSearchMatch = isCellSearchMatch(rowIdx, columnKey, value);
          
          // Check if this is the current search result
          const isCurrentSearchResult = isCellCurrentSearchResult(rowIdx, columnKey);
          
          // Determine if this specific cell is editable
          const isThisCellEditable = isCellEditable ? isCellEditable(rowIdx, columnKey) : editable;
          
          return (
            <SpreadsheetCell
              rowIdx={rowIdx}
              columnKey={columnKey}
              value={value}
              isSelected={isSelected}
              isActive={isActive}
              isSearchMatch={isSearchMatch}
              isCurrentSearchResult={isCurrentSearchResult}
              isEditable={isThisCellEditable}
              customStyle={customStyle}
              dropdownOptions={dropdownOptions?.[columnKey]}
              useTextareaDialog={textareaColumns?.includes(columnKey) || false}
              useCheckbox={checkboxColumns?.includes(columnKey) || false}
              useDatetime={datetimeColumns?.includes(columnKey) || false}
              useCommandPopover={commandPopoverColumns?.includes(columnKey) || false}
              commandPopoverOptions={commandPopoverOptions?.[columnKey]}
              onCellMouseDown={(r, c, e) => handleCellMouseDown(r, c, e, isThisCellEditable)}
              onCellMouseEnter={handleCellMouseEnter}
              onCellChange={handleCellChange}
              onCellBlur={(r, c, v) => handleCellBlurWithSave(r, c, v, onCellBlur)}
              onInputKeyDown={handleInputKeyDown}
              onCellFocus={handleCellFocus}
            />
          );
        }
      } as Column<SpreadsheetData>;
    })
  ];

  return (
    <Card className={`overflow-hidden ${fillHeight ? 'h-[85vh]' : ''} ${className}`}>
      <div 
        ref={containerRef}
        className={`spreadsheet-container relative bg-white ${(fillHeight || isFullscreen) ? 'h-[85vh]' : ''}`}
        onMouseUp={handleMouseUp}
        onKeyDown={handleSpreadsheetKeyDown}
        onMouseDown={handleContainerMouseDown}
        onClick={handleContainerClick}
        onContextMenu={handleContextMenu}
        tabIndex={0}
        style={(fillHeight || isFullscreen) ? { height: `${getSpreadsheetHeight()}px` } : undefined}
      >
        {showSearchBar && (
          <SpreadsheetSearchBar
            searchTerm={effectiveSearchTerm}
            onSearchChange={handleSearchChange}
            currentIndex={effectiveCurrentIndex}
            totalResults={effectiveTotalResults}
            onNextSearch={enhancedHandleNextSearch}
            onPreviousSearch={enhancedHandlePreviousSearch}
            onClearSearch={handleClearSearch}
            onClose={() => {
              setShowSearchBar(false);
              if (containerRef.current) {
                containerRef.current.focus();
              }
            }}
            spreadsheetId={spreadsheetId}
          />
        )}

        <SpreadsheetContextMenu
          isVisible={contextMenu.isVisible}
          position={contextMenu.position}
          selectedCellsCount={selectedCells.size}
          onExportSelection={exportSelectionToCSV}
          onClose={closeContextMenu}
          customMenuItems={customContextMenuItems}
          rightClickedRowData={contextMenu.rightClickedRowData}
          rightClickedRowIndex={contextMenu.rightClickedRowIndex}
          rightClickedColumnKey={contextMenu.rightClickedColumnKey}
        />
        
        <style>{`
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          
          .spreadsheet-container .rdg {
            border: none;
            background-color: white;
            color: #1f2937;
          }
          
          .dark .spreadsheet-container .rdg {
            background-color: white;
            color: #1f2937;
          }
          
          .spreadsheet-container .rdg-cell {
            border-right: 0.5px solid #D4D4D4;
            border-bottom: 0.5px solid #D4D4D4;
            user-select: none;
            padding: 0;
            background-color: white;
            color: #1f2937;
          }
          
          .spreadsheet-container .rdg-cell:not(.spreadsheet-row-header) {
            padding: 0;
          }
          
          .spreadsheet-container .rdg-cell input {
            user-select: text;
            cursor: text;
          }
          
          .spreadsheet-container .rdg-header-row {
            background-color: #f9fafb;
            border-bottom: 0.5px solid #D4D4D4;
          }
          
          .spreadsheet-container .rdg-row:hover .rdg-cell:not(.spreadsheet-row-header) {
            background-color: #f3f4f6;
          }
          
          .spreadsheet-container .spreadsheet-row-header {
            background-color: #f9fafb;
            border-right: 0.5px solid #D4D4D4;
          }
          
          .spreadsheet-container .rdg-cell:focus-within {
            outline: none;
          }

          .spreadsheet-container .spreadsheet-cell-selected {
            position: relative;
          }

          .spreadsheet-container .spreadsheet-cell-selected::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(66, 133, 244, 0.15);
            pointer-events: none;
            z-index: 1;
          }

          .spreadsheet-container:focus {
            outline: 2px solid hsl(var(--ring));
            outline-offset: -2px;
          }

          .spreadsheet-container .search-highlight {
            animation: searchPulse 0.5s ease-in-out;
          }

          @keyframes searchPulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }

          ${fillHeight || isFullscreen ? `
            .spreadsheet-container .rdg {
              height: 85vh !important;
            }
          ` : ''}
        `}</style>
        <DataGrid
          columns={columnDefs}
          rows={spreadsheetData}
          onRowsChange={handleRowsChange}
          rowHeight={rowHeight}
          headerRowHeight={showColumnHeaders ? 35 : 0}
          style={{
            height: (fillHeight || isFullscreen) ? '85vh' : getSpreadsheetHeight(),
          }}
          rowKeyGetter={(row: SpreadsheetData) => spreadsheetData.indexOf(row)}
          defaultColumnOptions={{
            sortable: false,
            resizable: true,
          }}
        />
      </div>
    </Card>
  );
}

export default Spreadsheet; 